import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './styles/header.css';
import parlaLogo from '../images/logo/parla-logo.webp';
import CTAKnap, { CTAIkoner } from './ctaknap';
import HamburgerMenu from './hamburgermenu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  // Helper function to determine if a nav link is active
  const isActiveLink = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    if (href === '/artikler') {
      return location.pathname === '/artikler' || location.pathname.startsWith('/artikler/');
    }
    return location.pathname === href;
  };

  useEffect(() => {
    // Check if notification was previously closed in this session
    const notificationClosed = sessionStorage.getItem('notificationClosed_15percent');
    
    if (!notificationClosed) {
      // Show notification after 2.5 seconds
      const timer = setTimeout(() => {
        setShowNotification(true);
        // Small delay to ensure DOM is updated before adding animation class
        setTimeout(() => {
          setNotificationVisible(true);
        }, 50);
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Prevent body scroll when menu is open
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Close menu when clicking on links
  const handleLinkClick = () => {
    if (isMenuOpen) {
      closeMenu();
    }
  };

  // Close menu on escape key and outside clicks
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        closeMenu();
      }
    };
    
    const handleClickOutside = (e) => {
      // If menu is open and click is outside nav and menu button, close menu
      if (isMenuOpen && 
          !e.target.closest('.nav') && 
          !e.target.closest('.mobile-menu-btn')) {
        closeMenu();
      }
    };
    
    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  const closeNotification = () => {
    setNotificationVisible(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setShowNotification(false);
    }, 400);
    // Store in sessionStorage so it only applies to current session
    sessionStorage.setItem('notificationClosed_15percent', 'true');
  };

  return (
    <>
      {/* Notification Bar */}
      {showNotification && (
        <div className={`notification-bar ${notificationVisible ? 'notification-bar--visible' : ''}`}>
          <div className="container">
            <div className="notification-slider">
              <div className="notification-slide">
                <span>Få 15% rabat på en mellem størrelse hjemmeside - Kun denne måned!</span>
                <span>Få 15% rabat på en mellem størrelse hjemmeside - Kun denne måned!</span>
                <span>Få 15% rabat på en mellem størrelse hjemmeside - Kun denne måned!</span>
                <span>Få 15% rabat på en mellem størrelse hjemmeside - Kun denne måned!</span>
              </div>
            </div>
            <button 
              className="notification-close"
              onClick={closeNotification}
              aria-label="Luk notifikation"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header className={`header ${!notificationVisible ? 'no-notification' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <a href="/">
                <img 
                  src={parlaLogo} 
                  alt="Parla Branding - Professionelle hjemmesider og SEO" 
                  className="logo-image" 
                />
              </a>
            </div>
            
            <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
              <a href="/" className={`nav-link ${isActiveLink('/') ? 'nav-link--active' : ''}`} onClick={handleLinkClick}>
                <span className="nav-text">Hjem</span>
              </a>
              <a href="/omos" className={`nav-link ${isActiveLink('/omos') ? 'nav-link--active' : ''}`} onClick={handleLinkClick}>
                <span className="nav-text">Om Os</span>
              </a>
              
              {/* Tjenester Dropdown */}
              <div 
                className="nav-dropdown"
                onMouseEnter={() => setIsDropdownOpen(true)}
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <a 
                  href="/tjenester" 
                  className={`nav-link nav-link-dropdown ${isActiveLink('/tjenester') || isActiveLink('/hjemmesider') || isActiveLink('/seo') ? 'nav-link--active' : ''}`} 
                  onClick={handleLinkClick}
                >
                  <span className="nav-text">Tjenester</span>
                  <CTAIkoner.ChevronDown className="dropdown-icon" />
                </a>
                <div className={`dropdown-menu ${isDropdownOpen ? 'dropdown-menu--open' : ''}`}>
                  <a href="/tjenester" className="dropdown-link" onClick={handleLinkClick}>
                    <div className="dropdown-item">
                      <div className="dropdown-item-icon">
                        <CTAIkoner.Globe />
                      </div>
                      <div className="dropdown-item-content">
                        <div className="dropdown-item-title">Alle Tjenester</div>
                        <div className="dropdown-item-description">Se vores komplette serviceudbud</div>
                      </div>
                    </div>
                  </a>
                  <a href="/hjemmesider" className="dropdown-link" onClick={handleLinkClick}>
                    <div className="dropdown-item">
                      <div className="dropdown-item-icon">
                        <CTAIkoner.Monitor />
                      </div>
                      <div className="dropdown-item-content">
                        <div className="dropdown-item-title">Hjemmesider</div>
                        <div className="dropdown-item-description">Professionelle websites der konverterer</div>
                      </div>
                    </div>
                  </a>
                  <a href="/seo" className="dropdown-link" onClick={handleLinkClick}>
                    <div className="dropdown-item">
                      <div className="dropdown-item-icon">
                        <CTAIkoner.BarChart />
                      </div>
                      <div className="dropdown-item-content">
                        <div className="dropdown-item-title">SEO</div>
                        <div className="dropdown-item-description">Søgemaskineoptimering der virker</div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              
              <a href="/priser" className={`nav-link ${isActiveLink('/priser') ? 'nav-link--active' : ''}`} onClick={handleLinkClick}>
                <span className="nav-text">Priser</span>
              </a>
              <a href="/cases" className={`nav-link ${isActiveLink('/cases') ? 'nav-link--active' : ''}`} onClick={handleLinkClick}>
                <span className="nav-text">Cases</span>
              </a>
            </nav>
            
            <div className="header-actions">
              <CTAKnap 
                variant="outline" 
                href="/tjenester"
                icon={<CTAIkoner.Search />}
                ariaLabel="Se vores tjenester"
              >
                Se vores tjenester
              </CTAKnap>
              <CTAKnap 
                variant="primary" 
                href="/kontakt"
                icon={<CTAIkoner.ArrowRight />}
                ariaLabel="Kontakt os"
              >
                Kontakt os
              </CTAKnap>
            </div>
            
            <HamburgerMenu />
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
